import type { ColumnDef } from '@tanstack/react-table'
import { MENU_COLUMN_ID } from './consts'

/**
 * A vehicle list's column definition. The value type stays open the way
 * `DataTable` keeps it open -- a table's columns hold whatever their own rows
 * hold, and tanstack has no narrower type for a mixed list.
 */
export type VehicleColumnDef<T> = ColumnDef<T, any>

export interface VehicleColumnSetting {
  displayName: string
  enabled: boolean
  order: number
}

export type VehicleColumnConfig = ReadonlyMap<string, VehicleColumnSetting>

interface ColumnEdge {
  node: {
    name: string
    displayName: string
    enabled: boolean
    order: number
  }
}

export function buildVehicleColumnConfig(
  edges: ColumnEdge[]
): VehicleColumnConfig {
  return new Map(
    edges.map(({ node }) => [
      node.name,
      {
        displayName: node.displayName,
        enabled: node.enabled,
        order: node.order,
      },
    ])
  )
}

/** A column's id, which is what a DynamicColumn row keys on. */
function columnId<T>(column: VehicleColumnDef<T>): string | undefined {
  if (column.id) return column.id
  return 'accessorKey' in column ? String(column.accessorKey) : undefined
}

/**
 * The columns a vehicle list renders, in the order and under the headers the
 * company configured.
 *
 * Both the fleet-wide page and the tab on the vehicle itself run their column
 * definitions through this, which is what keeps the two showing the same
 * columns in the same order under the same names.
 *
 * A column the configuration does not mention keeps its declared position and
 * header -- that covers a column added after the seeds ran, and a company
 * whose seed has not run at all, where hiding on a miss would blank the table.
 */
export function resolveVehicleColumns<T>(
  columns: VehicleColumnDef<T>[],
  config: VehicleColumnConfig
): VehicleColumnDef<T>[] {
  if (config.size === 0) return columns

  const ordered = columns
    .map((column, index) => ({ column, index, id: columnId(column) }))
    .filter(({ id }) => {
      if (id === MENU_COLUMN_ID || id === undefined) return true
      const setting = config.get(id)
      return setting ? setting.enabled : true
    })
    .sort((a, b) => sortKey(a, config) - sortKey(b, config))

  return ordered.map(({ column, id }) => {
    const displayName = id ? config.get(id)?.displayName : undefined
    return displayName ? { ...column, header: displayName } : column
  })
}

// Unconfigured columns sort after the configured ones, in their declared
// order, and the row menu stays last whatever the configuration says.
const UNCONFIGURED_OFFSET = 1000
const MENU_OFFSET = 2000

function sortKey<T>(
  entry: {
    column: VehicleColumnDef<T>
    index: number
    id: string | undefined
  },
  config: VehicleColumnConfig
): number {
  if (entry.id === MENU_COLUMN_ID) return MENU_OFFSET + entry.index
  const setting = entry.id ? config.get(entry.id) : undefined
  if (!setting) return UNCONFIGURED_OFFSET + entry.index
  return setting.order
}
